import {
    CLICK_START_TRIAL_ADDITIONAL_DATA_STORAGE_KEY,
    CURRENT_REFERER_STORAGE_KEY,
    QA_DOMAINS
} from '../constants/constants';
import * as queryString from 'query-string';
import CookiesService from './CookiesService';
import * as DOMPurify from 'dompurify';
import StorageService from './StorageService';
import type { TAdditionalDataForClickStartTrialModules } from '../types/util-types';
import type { TFeaturesCheckListAnalyticsData } from '../components/Hero/FeatureChecklistHero/constants-and-types';
import DeviceService from './DeviceService';
import { Coupons } from '../constants/promoCodes';
import { v4 as uuid } from 'uuid';
import { STATSIG_ID_COOKIE } from '../../static/ab-test-shared-constants';
import { trackError } from '../utils/error.utils';

export interface Display {
    sm?: string;
    md?: string;
    lg?: string;
    xl?: string;
}

export interface AdvertisingData {
    utm_campaign: string | null;
    utm_content: string | null;
    utm_medium: string | null;
    utm_source: string | null;
    utm_term: string | null;
    utm_initiative: string | null;
    li_fat_id: string | null;
    code: string | null;
    utm_origin: string | null;
    referral_source: string | null;
    gclid: string | null;
    fbclid: string | null;
    irclickid: string | null;
    placement: string | null;
    promo: string | null;
    [key: string]: string | null;
}
declare global {
    interface Window {
        mixpanel: any;
        analytics: {
            user(): {
                id: () => string;
            };
            identify(
                userId: string | undefined,
                data: Record<string, any> | undefined,
                integrations: Record<string, any>
            ): any;
            track(
                eventName: string,
                data: Record<string, any>,
                integrations: Record<string, any>
            ): any;
            page(name, properties, integrations): any;
            reset(): any;
        };
    }
}

const osData = [
    { os: 'Windows 10', regex: /(Windows 10.0|Windows NT 10.0)/ },
    { os: 'Windows 8.1', regex: /(Windows 8.1|Windows NT 6.3)/ },
    { os: 'Windows 8', regex: /(Windows 8|Windows NT 6.2)/ },
    { os: 'Windows 7', regex: /(Windows 7|Windows NT 6.1)/ },
    { os: 'Windows Vista', regex: /Windows NT 6.0/ },
    { os: 'Windows Server 2003', regex: /Windows NT 5.2/ },
    { os: 'Windows XP', regex: /(Windows NT 5.1|Windows XP)/ },
    { os: 'Windows 2000', regex: /(Windows NT 5.0|Windows 2000)/ },
    { os: 'Windows ME', regex: /(Win 9x 4.90|Windows ME)/ },
    { os: 'Windows 98', regex: /(Windows 98|Win98)/ },
    { os: 'Windows 95', regex: /(Windows 95|Win95|Windows_95)/ },
    {
        os: 'Windows NT 4.0',
        regex: /(Windows NT 4.0|WinNT4.0|WinNT|Windows NT)/
    },
    { os: 'Windows CE', regex: /Windows CE/ },
    { os: 'Windows 3.11', regex: /Win16/ },
    { os: 'Android', regex: /Android/ },
    { os: 'Open BSD', regex: /OpenBSD/ },
    { os: 'Sun OS', regex: /SunOS/ },
    { os: 'Chrome OS', regex: /CrOS/ },
    { os: 'Linux', regex: /(Linux|X11(?!.*CrOS))/ },
    { os: 'iOS', regex: /(iPhone|iPad|iPod)/ },
    { os: 'Mac OS X', regex: /Mac OS X/ },
    {
        os: 'Mac OS',
        regex: /(Mac OS|MacPPC|MacIntel|Mac_PowerPC|Macintosh)/
    },
    { os: 'QNX', regex: /QNX/ },
    { os: 'UNIX', regex: /UNIX/ },
    { os: 'BeOS', regex: /BeOS/ },
    { os: 'OS/2', regex: /OS\/2/ },
    {
        os: 'Search Bot',
        regex: /(nuhk|Googlebot|Yammybot|Openbot|Slurp|MSNBot|Ask Jeeves\/Teoma|ia_archiver)/
    }
];
let _advertisingData: AdvertisingData | undefined;

export default class UtilsService {
    static getParameterByName = (name: string): string | null => {
        let searchParam;
        if (typeof window !== 'undefined') {
            searchParam = window.location.search;
        } else {
            searchParam = '';
        }

        let result = queryString.parse(searchParam)[name];

        if (Array.isArray(result)) {
            [result] = result;
        }

        return result;
    };

    static convertObjectToQueryString = (
        rawObject: Record<string, unknown>
    ): string => queryString.stringify(rawObject);

    static getUtmParam = (param: keyof AdvertisingData): string | undefined => {
        const utmParams = this.getAllUtmParams();
        return utmParams[param];
    };

    static getSessionUtmParams(): AdvertisingData | undefined {
        return _advertisingData;
    }

    static getAllUtmParams(): AdvertisingData | {} {
        let utmParams = _advertisingData;
        if (!utmParams) {
            const utmParamsJSON = CookiesService.getCookie('utm_params');
            if (utmParamsJSON) {
                utmParams = JSON.parse(utmParamsJSON);
            }
        }
        return utmParams || {};
    }

    static storeInitialUtms = (promoCode?: string): void => {
        try {
            const urlUtmData = this.getAdvertisingData();
            const memoryUtmData = _advertisingData || {};

            let cookieUtmData = {};
            try {
                const cookieUtmDataJSON =
                    CookiesService.getCookie('utm_params');
                if (cookieUtmDataJSON) {
                    const parsedData = JSON.parse(cookieUtmDataJSON);
                    if (typeof parsedData === 'object' && parsedData !== null) {
                        cookieUtmData = parsedData;
                    }
                }
            } catch (parseError) {
                trackError(parseError, {
                    message: 'failed to parse utm params from cookie'
                });
            }

            if (promoCode && promoCode !== Coupons.nopromo) {
                urlUtmData.code = promoCode;
            }

            const mergedUtmData: AdvertisingData = {
                ...cookieUtmData,
                ...memoryUtmData,
                ...urlUtmData
            };

            const hasAnyUtmData = Object.values(mergedUtmData).some(
                value => value !== null
            );

            if (hasAnyUtmData) {
                _advertisingData = mergedUtmData;

                CookiesService.setCookie({
                    cookieName: 'utm_params',
                    cookieValue: JSON.stringify(mergedUtmData),
                    expirationDays: 1
                });
            }
        } catch (error) {
            trackError(error, {
                message: 'unable to store utms'
            });
        }
    };
    static redirectWithUTM = (
        targetUrl: string,
        includeAllQueryParams: boolean = false
    ): void => {
        if (typeof window === 'undefined') return;

        const [baseUrl, existingQuery] = targetUrl.split('?');
        const currentParams = new URLSearchParams(window.location.search);
        const mergedParams = new URLSearchParams(existingQuery || '');

        currentParams.forEach((value, key) => {
            if (includeAllQueryParams || this.STANDARD_UTM_KEYS.has(key)) {
                mergedParams.set(key, value);
            }
        });

        const query = mergedParams.toString();
        window.location.href = query ? `${baseUrl}?${query}` : baseUrl;
    };

    static isCurrentUrlInList = (urlList: string[]): boolean => {
        const pathname = window.location.pathname;
        let returnValue = false;

        // Special logic for homepage ('/get' for paid home page)
        if (urlList.includes('/')) {
            urlList.splice(urlList.indexOf('/'), 1);
            if (pathname === '/' || pathname === '/get') {
                returnValue = true;
            }
        }

        if (
            !returnValue &&
            urlList.some(
                pathPrefix => pathname && pathname.startsWith(pathPrefix)
            )
        ) {
            returnValue = true;
        }
        return returnValue || UtilsService.isInTemplatePreviewPage();
    };

    static storeCoupon = (promo?: string): void => {
        const coupon = promo || this.getCouponFromQueryParams();
        const isCouponValid = coupon && coupon !== Coupons.nopromo;

        if (isCouponValid) {
            StorageService.setItem({
                key: 'HB_COUPON',
                data: coupon,
                storageType: 'sessionStorage'
            });
        }
    };

    static getCouponFromQueryParams(): string | null {
        return (
            this.getParameterByName('promo') || this.getParameterByName('code')
        );
    }

    static getCouponFromCookies(): string | null {
        const utmParamsJSON = CookiesService.getCookie('utm_params');
        if (utmParamsJSON) {
            const utmParams = JSON.parse(utmParamsJSON);
            return utmParams.promo || utmParams.code;
        }
        return null;
    }

    static getReferralCodeFromCookies(): string | null {
        const utmParamsJSON = CookiesService.getCookie('utm_params');
        if (utmParamsJSON) {
            const utmParams = JSON.parse(utmParamsJSON);
            return utmParams.code;
        }
        return null;
    }

    static convertDiscountStringToNumber(str: string): number | null {
        if (typeof str === 'string') {
            const match = str.match(/(\d+(?:\.\d+)?)%/);
            const number = match ? parseFloat(match[1]) : null;
            return number;
        }
        return null;
    }

    static isProduction = (): boolean =>
        process.env && process.env.GATSBY_env === 'production';

    static isEmailValid = (email: string): boolean =>
        /^[\w%+.-]+@[\d.a-z-]+\.[a-z]{2,}$/i.test(email);

    static isQA = (): boolean =>
        QA_DOMAINS.some(domain => window.location.host.includes(domain));

    static isStaging = (): boolean =>
        process.env.GATSBY_env === 'staging' && !this.isQA();

    static isDev = (): boolean => process.env.GATSBY_env === 'development';

    private static readonly UTM_PARAM_KEYS = [
        'utm_campaign',
        'utm_content',
        'utm_medium',
        'utm_source',
        'utm_term',
        'utm_initiative',
        'li_fat_id',
        'code',
        'utm_origin',
        'referral_source',
        'gclid',
        'fbclid',
        'irclickid',
        'placement',
        'utm_placement',
        'promo'
    ] as const;

    private static readonly STANDARD_UTM_KEYS = new Set<string>(
        this.UTM_PARAM_KEYS.filter(
            key => key.startsWith('utm_') && key !== 'utm_placement'
        )
    );

    private static createEmptyUtmData(): AdvertisingData {
        return {
            utm_campaign: null,
            utm_content: null,
            utm_medium: null,
            utm_source: null,
            utm_term: null,
            utm_initiative: null,
            li_fat_id: null,
            code: null,
            utm_origin: null,
            referral_source: null,
            gclid: null,
            fbclid: null,
            irclickid: null,
            placement: null,
            promo: null
        };
    }

    static getAdvertisingData = (): AdvertisingData => {
        if (typeof window === 'undefined') {
            return this.createEmptyUtmData();
        }

        const urlParams = new URLSearchParams(window.location.search);
        const utmParams = {};
        const standardUtmKeys = new Set(
            this.UTM_PARAM_KEYS as readonly string[]
        );

        this.UTM_PARAM_KEYS.forEach(key => {
            const value = urlParams.get(key);
            if (value && value.trim() !== '') {
                utmParams[key] = value.trim();
            }
        });

        const otherUtms: Record<string, string> = {};
        Object.entries(Object.fromEntries(urlParams)).forEach(
            ([key, value]) => {
                if (standardUtmKeys.has(key) || !value || value.trim() === '')
                    return;
                otherUtms[key] = value.trim();
            }
        );

        return { ...utmParams, ...otherUtms } as AdvertisingData;
    };

    static getSignupLandingPageFromLocationPath = (): string => {
        const locationPathname = window.location.pathname;
        let signupLandingPage = locationPathname.split('?')[0];

        if (signupLandingPage[0] === '/')
            signupLandingPage = signupLandingPage.substring(1);

        if (signupLandingPage === '') {
            signupLandingPage = 'home';
        }

        return signupLandingPage;
    };

    static sanitizeHtml = (string: string): string => {
        return DOMPurify.sanitize(string);
    };

    static toCamelCase = (string: string): string => {
        const returnString = string
            .toLowerCase()
            .replace(/(^.)|([\s_-]+.)/g, match =>
                match.charAt(match.length - 1).toUpperCase()
            );

        return returnString.replace(/^./, string[0].toLowerCase());
    };

    static debounce(callback: () => void, wait: number): () => void {
        let timeout: number;

        return (...args: []) => {
            // const context = this

            clearTimeout(timeout);

            timeout = window.setTimeout(() => callback.apply(this, args), wait);
        };
    }

    static throttle(func: () => void, timeFrame: number): () => void {
        let lastTime = 0;

        return () => {
            // TS can't coerce Date to number
            const now = new Date() as unknown as number;

            if (now - lastTime >= timeFrame) {
                func();

                lastTime = now;
            }
        };
    }

    static formatReviewsNumber = (reviewsNumber: number): string => {
        if (reviewsNumber < 1000) {
            return reviewsNumber.toString();
        } else if (reviewsNumber < 1000000) {
            return `${(reviewsNumber / 1000).toFixed(1)}K`;
        }
        return `${(reviewsNumber / 1000000).toFixed(1)}M`;
    };

    static formatPhoneNumber = (phoneNo: string) => {
        const x = phoneNo
            .replace(/\D/g, '')
            .match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
        if (!x) {
            return '';
        }

        const formattedPhoneNumber = !x[2]
            ? x[1]
            : '(' + x[1] + ') ' + x[2] + (x[3] ? '-' + x[3] : '');
        return formattedPhoneNumber;
    };

    static scrollToElementId = (elementId: string, buffer?: number): void => {
        const navBar = document.getElementsByClassName('navbar');
        const firstNavBarItem = navBar[0];

        // offsetHeight exists only on HTMLElement, does not exists on SVGElement
        if (firstNavBarItem instanceof HTMLElement) {
            const navBarOffset = firstNavBarItem.offsetHeight;

            const bodyRect = document.body
                ? document.body.getBoundingClientRect().top
                : 0;
            const element = document.getElementById(elementId);

            if (element !== null) {
                const elementRect = element.getBoundingClientRect().top;
                const elementPosition = elementRect - bodyRect;
                const offsetPosition = buffer
                    ? elementPosition - navBarOffset - buffer
                    : elementPosition - navBarOffset;

                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                });
            } else {
                console.warn(`Element with id '${elementId}' not found.`);
            }
        } else {
            console.warn(`Navbar element not found.`);
        }
    };

    static numberWithCommas = (number: number) =>
        number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    static screens = (display?: Display): string => {
        if (typeof display === 'undefined') {
            return '';
        }

        let css = '';

        const ownProps = Object.keys(display);

        let i = ownProps.length;

        const entries = new Array(i);

        while (i--) {
            entries[i] = [ownProps[i], display[ownProps[i]]];
        }

        entries.forEach(value => {
            css += `display-for--${value.join('-')} `;
        });

        return css;
    };

    static getKeyWithLargestValue = (
        object: Record<string, number | string>
    ): boolean | string => {
        if (typeof object === 'object') {
            const objectKeys = Object.keys(object);

            return objectKeys.reduce(
                (accumulator: string, key: string): string =>
                    object[key] >= object[accumulator] ? key : accumulator,
                objectKeys[0]
            );
        }

        return false;
    };

    static isBrowser() {
        return typeof window !== 'undefined';
    }

    static handleThirdPartyLoad(cb: () => void, checkIsLoaded: () => boolean) {
        if (checkIsLoaded()) {
            cb();
        } else {
            setTimeout(() => {
                UtilsService.handleThirdPartyLoad(cb, checkIsLoaded);
            }, 200);
        }
    }

    static shuffle = array => {
        for (let i = array.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [array[i], array[j]] = [array[j], array[i]];
        }
        return array;
    };

    static getOrigin(): string {
        if (!this.isBrowser()) return 'https://www.honeybook.com';
        return window.location.origin;
    }

    static getCurrentUrl(): string {
        return window.location.origin + window.location.pathname;
    }

    static getCurrentPathname(): string {
        return window.location.pathname;
    }

    static addAdditionalDataToClickStartTrial(
        data: TFeaturesCheckListAnalyticsData,
        module: TAdditionalDataForClickStartTrialModules
    ) {
        const additionalData =
            StorageService.getItem(
                CLICK_START_TRIAL_ADDITIONAL_DATA_STORAGE_KEY,
                'sessionStorage'
            ) || {};

        additionalData[window.location.pathname] =
            additionalData[window.location.pathname] || {};
        additionalData[window.location.pathname][module] = data;
        StorageService.setItem({
            key: CLICK_START_TRIAL_ADDITIONAL_DATA_STORAGE_KEY,
            data: additionalData,
            storageType: 'sessionStorage'
        });
    }

    private static getUrlInstance(path: string): URL {
        return new URL(path);
    }

    static getUrlPathname(path: string) {
        if (!path) {
            return undefined;
        }
        const url = this.getUrlInstance(path);
        return url.pathname;
    }

    static getOsData() {
        let os = 'unknown';
        let osVersion = 'unknown';
        const ua = navigator.userAgent;

        for (const currentOs of osData) {
            if (currentOs.regex.test(ua)) {
                os = currentOs.os;
                break;
            }
        }

        if (/Windows/.test(os)) {
            const regexResultForOsVersion = /Windows (.*)/.exec(ua);
            osVersion = regexResultForOsVersion?.[1] ?? osVersion;
            os = 'Windows';
        }

        switch (os) {
            case 'Mac OS':
            case 'Mac OS X':
                const regexResultForOsVersion =
                    /(?:Mac OS|Mac OS X|MacPPC|MacIntel|Mac_PowerPC|Macintosh) ([\.\_\d]+)/.exec(
                        ua
                    );
                osVersion = regexResultForOsVersion?.[1] ?? osVersion;
                break;
            case 'Android':
                osVersion = DeviceService.getMobileOs()
                    .android_version as string;
                break;
            case 'iOS':
                osVersion = DeviceService.getMobileOs().ios_version as string;
                break;
        }
        osVersion = osVersion.replace(/_/g, '.');
        return { $os: os, os_version: osVersion };
    }

    static extractDomain(path: string) {
        if (!path) {
            return undefined;
        }
        const url = this.getUrlInstance(path);
        return url.hostname;
    }

    static getScreenResolution(): string {
        if (UtilsService.isBrowser()) {
            const width = window.screen.width * window.devicePixelRatio;
            const height = window.screen.height * window.devicePixelRatio;
            return `${Math.min(width, height)}x${Math.max(width, height)}`;
        }
        return 'N/A';
    }

    static getPriceAfterDiscount(
        price: number,
        discountValue = 0,
        fixedPrice = 0,
        fractionDigits = 2
    ) {
        if (fixedPrice > 0) {
            return fixedPrice;
        }
        if (discountValue <= 0) {
            return price;
        }
        let result = +(price - (discountValue / 100) * price);
        if (result % 1 !== 0) {
            return result.toFixed(fractionDigits);
        }
        return result;
    }

    static validateReferrer() {
        const { lastUpdated } =
            StorageService.getItem(
                CURRENT_REFERER_STORAGE_KEY,
                'sessionStorage'
            ) || {};
        if (Date.now() - lastUpdated > 1000 * 60 * 60) {
            StorageService.removeItem(
                CURRENT_REFERER_STORAGE_KEY,
                'sessionStorage'
            );
        }
    }

    static updateReferrer() {
        //help us indicate our current referrer because this code runs before we navigate to new url
        StorageService.setItem({
            key: CURRENT_REFERER_STORAGE_KEY,
            data: {
                referrer: this.getCurrentUrl(),
                lastUpdated: Date.now()
            },
            storageType: 'sessionStorage'
        });
    }

    static MEDIUM_SCREEN_BREAKPOINT = '768px';

    static isInTemplatePreviewPage() {
        return /\/template[-_]preview/.test(window.location.pathname);
    }

    static readonly HB_ANONYMOUS_ID_COOKIE_NAME = 'hb_anonymous_id';

    static handleHBAnonymousId() {
        let cookieDomain = '.honeybook.com';
        if (this.isDev()) {
            cookieDomain = 'localhost';
        } else if (this.isStaging()) {
            cookieDomain = '.hbdev.io';
        }

        const queryParamStableId = this.getParameterByName(
            this.HB_ANONYMOUS_ID_COOKIE_NAME
        );
        let hbAnonymousId = CookiesService.getCookie(
            this.HB_ANONYMOUS_ID_COOKIE_NAME
        );

        if (!hbAnonymousId) {
            hbAnonymousId =
                CookiesService.getCookie(STATSIG_ID_COOKIE) ||
                queryParamStableId ||
                uuid();

            CookiesService.setCookie({
                cookieName: this.HB_ANONYMOUS_ID_COOKIE_NAME,
                cookieValue: hbAnonymousId,
                domain: cookieDomain
            });
        }
        return hbAnonymousId;
    }

    static getClientPortalAwsBucket() {
        return this.isProduction()
            ? 'hb-client-portal'
            : 'hb-client-portal-dev';
    }

    static getClientPortalConfigurationAwsDomain({
        domain
    }: {
        domain: string;
    }) {
        return this.isDev()
            ? domain.indexOf('ngrok') > -1
                ? domain
                : 'localhost:3000'
            : domain;
    }

    static getStagingNameFromUrl(url: string) {
        if (QA_DOMAINS.some(domain => url.includes(domain))) {
            return 'qa';
        }
        return url.replace(/.*hb-app-staging-([^.]+).*/, (_, arg1: string) => {
            return arg1;
        });
    }

    static getEnvNameForS3() {
        if (this.isProduction()) {
            return 'prod';
        } else if (this.isStaging() || this.isQA()) {
            return this.getStagingNameFromUrl(window.location.origin);
        } else {
            return process.env.USER || 'local';
        }
    }

    static getPublicConfigurationAwsDomain() {
        return this.isProduction()
            ? 'https://public-content.honeybook.com'
            : 'https://public-content.hbdev.io';
    }

    static getS3UrlPrefixForPublicTemplateData() {
        return this.isProduction()
            ? 'https://drz60ich0zu0v.cloudfront.net'
            : 'https://d258zsheehsnq8.cloudfront.net';
    }

    // Utility function to calculate label based on first letter
    static calculateVowelLabel = (
        label: string | null | undefined,
        baseKey: string,
        t: (key: string) => string
    ): string => {
        if (!label) return t(baseKey);

        const startsWithVowel = /^[aeiou]/i.test(label);
        return startsWithVowel ? t(`${baseKey}Vowel`) : t(baseKey);
    };

    /**
     * Get the appropriate redirect URL - always redirects signup and login to app domain
     * Redirects to app.honeybook.com in production or hb-mx-staging in e2e environments
     * @param path - The path to redirect to (e.g., '/signup', '/signup/discovery', '/app/login')
     * @returns The full URL to redirect to
     */
    static getRedirectUrl = (path: string): string => {
        // Preserve URL params (query string) from the input path
        const [pathname, search = ''] = path.includes('?')
            ? path.split('?', 2)
            : [path, ''];
        const searchString = search ? `?${search}` : '';

        // Always use app domain logic (no server-side rendering fallback)

        // Helper function to build signup redirect URL
        const buildSignupUrl = (signupPath: string): string => {
            if (this.isProduction()) {
                return `https://app.honeybook.com${signupPath}${searchString}`;
            } else if (this.isDev()) {
                return `http://localhost:7100${signupPath}${searchString}`;
            } else if (this.isBrowser()) {
                // For e2e environments, replace hb-app-staging with hb-mx-staging
                const origin = window.location.origin;
                const irfOrigin = origin.replace(
                    'hb-app-staging',
                    'hb-mx-staging'
                );
                return `${irfOrigin}${signupPath}${searchString}`;
            } else {
                // During server-side rendering, default to production app URL
                return `https://app.honeybook.com${signupPath}${searchString}`;
            }
        };

        // For signup/discovery path, always redirect to app domain
        if (
            pathname === '/signup/discovery' ||
            pathname.startsWith('/signup/discovery')
        ) {
            return buildSignupUrl('/signup/discovery');
        }

        // For other signup paths, always redirect to app domain
        if (pathname === '/signup' || pathname.startsWith('/signup/')) {
            return buildSignupUrl('/signup');
        }

        // For login paths, always redirect to app domain
        if (pathname === '/app/login' || pathname.includes('/app/login')) {
            if (this.isProduction()) {
                return `https://app.honeybook.com/app/login${searchString}`;
            } else if (this.isDev()) {
                return `http://localhost:7100/app/login${searchString}`;
            } else if (this.isBrowser()) {
                // For e2e environments, replace hb-app-staging with hb-mx-staging
                const origin = window.location.origin;
                const irfOrigin = origin.replace(
                    'hb-app-staging',
                    'hb-mx-staging'
                );
                return `${irfOrigin}/app/login${searchString}`;
            } else {
                // During server-side rendering, default to production app URL
                return `https://app.honeybook.com/app/login${searchString}`;
            }
        }

        // For any other paths, use the current origin (if browser) or default behavior
        // path already includes searchString (pathname + search)
        const pathWithSearch = `${pathname}${searchString}`;
        if (this.isBrowser()) {
            return `${window.location.origin}${pathWithSearch}`;
        } else {
            // During server-side rendering, default to marketing site
            if (this.isProduction()) {
                return `https://www.honeybook.com${pathWithSearch}`;
            } else if (this.isDev()) {
                return `http://localhost:8000${pathWithSearch}`;
            } else {
                return pathWithSearch;
            }
        }
    };

    /**
     * Check if a URL is absolute or protocol-relative (http(s) or //)
     */
    static isAbsoluteUrl = (url: string): boolean => {
        return /^(https?:)?\/\//i.test(url);
    };

    /**
     * Build an absolute URL for navigation: pass through absolute/protocol-relative,
     * otherwise normalize and map relative paths via getRedirectUrl.
     */
    static getAbsoluteUrl = (url: string): string => {
        if (this.isAbsoluteUrl(url)) return url;
        const normalized = url.startsWith('/') ? url : `/${url}`;
        return this.getRedirectUrl(normalized);
    };

    /**
     * Redirect to URL with IRF cookie logic applied
     * @param path - The path to redirect to
     */
    static redirectTo = (path: string): void => {
        const redirectUrl = this.getRedirectUrl(path);
        window.location.assign(redirectUrl);
    };
}

export const breakPoints = {
    medium: 768,
    large: 992,
    xlarge: 1200
};
